import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder, getAssignableUsers, getAllEmployees, assignOrder, startOrder, updateOrder, downloadOrderPdf, completeOrder, getProjects, assignOrderToProject, createOrder } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { StatusBadge } from '../components/StatusBadge';
import { formatOrderNo, formatDate, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Calendar, User, Loader2, Edit2, Plus, Trash2, Save, X, FileText, Check, XCircle, Download, ChevronLeft, ChevronRight, Play, CheckCircle, Users, Package, Clock, FolderKanban, Copy } from 'lucide-react';

export const OrderDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAdmin, user } = useAuth();
    const [order, setOrder] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]); // All employees for helper selection
    const [loading, setLoading] = useState(true);
    const [assigning, setAssigning] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageIndex, setImageIndex] = useState(0);
    
    // Project assignment state
    const [projects, setProjects] = useState([]);
    const [showProjectDialog, setShowProjectDialog] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [assigningProject, setAssigningProject] = useState(false);
    
    // Complete order modal state
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [completeData, setCompleteData] = useState({
        started_at: '',
        finished_at: '',
        labor_hours: '',
        travel_hours: '0',
        notes: '',
        work_items: [],
        helper_user_ids: []
    });
    
    // Follow-up order dialog state
    const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
    const [incompleteWorkItems, setIncompleteWorkItems] = useState([]);
    const [creatingFollowUp, setCreatingFollowUp] = useState(false);
    
    // Additional work and materials state
    const [additionalWork, setAdditionalWork] = useState([]);
    const [usedMaterials, setUsedMaterials] = useState([]);

    const loadData = async () => {
        try {
            const [orderRes, employeesRes] = await Promise.all([
                getOrder(id),
                isAdmin() ? getAssignableUsers() : Promise.resolve({ data: [] })
            ]);
            setOrder(orderRes.data);
            setEmployees(employeesRes.data);
            
            // Always load all employees for helper name resolution (even for non-admins)
            try {
                const allEmployeesRes = await getAllEmployees();
                setAllEmployees(allEmployeesRes.data);
            } catch {
                setAllEmployees(employeesRes.data);
            }
            
            // Load projects separately to avoid blocking the main load (admin only)
            if (isAdmin()) {
                try {
                    const [openProjects, inProgressProjects] = await Promise.all([
                        getProjects({ status: 'OPEN' }).catch(() => ({ data: [] })),
                        getProjects({ status: 'IN_PROGRESS' }).catch(() => ({ data: [] }))
                    ]);
                    setProjects([
                        ...(openProjects.data || []),
                        ...(inProgressProjects.data || [])
                    ]);
                } catch {
                    setProjects([]);
                }
            }
            
            if (orderRes.data.assigned_to_user_id) {
                setSelectedEmployee(orderRes.data.assigned_to_user_id);
            }
        } catch (err) {
            toast.error('Auftrag nicht gefunden');
            navigate('/orders');
        } finally {
            setLoading(false);
        }
    };
    
    const handleAssignProject = async () => {
        setAssigningProject(true);
        try {
            const projectIdToAssign = selectedProjectId === 'none' ? null : selectedProjectId;
            await assignOrderToProject(id, projectIdToAssign);
            toast.success(projectIdToAssign ? 'Projekt zugeordnet' : 'Vom Projekt entfernt');
            setShowProjectDialog(false);
            loadData();
        } catch (err) {
            toast.error('Fehler beim Zuordnen');
        } finally {
            setAssigningProject(false);
        }
    };

    // Load data when order ID changes
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleAssign = async () => {
        if (!selectedEmployee) {
            toast.error('Bitte wählen Sie eine Person');
            return;
        }
        setAssigning(true);
        try {
            if (selectedEmployee === 'unassign') {
                // Unassign the order
                await assignOrder(id, null);
                toast.success('Zuweisung entfernt');
            } else {
                await assignOrder(id, selectedEmployee);
                toast.success('Person zugewiesen');
            }
            setSelectedEmployee('');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Zuweisen');
        } finally {
            setAssigning(false);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            const response = await downloadOrderPdf(id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Auftrag_ORD-${String(order.order_no).padStart(4, '0')}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            toast.success('PDF heruntergeladen');
        } catch (err) {
            toast.error('Fehler beim Erstellen der PDF');
        }
    };

    const openCompleteModal = () => {
        const now = new Date();
        const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
        
        // Prüfen ob bereits work_items im State vorhanden sind (Zwischenspeicherung)
        const existingWorkItems = completeData.work_items;
        const orderWorkItemIds = (order.work_items || []).map(item => item.id);
        
        // Verwende existierende work_items wenn sie zum aktuellen Auftrag passen
        const hasMatchingWorkItems = existingWorkItems.length > 0 && 
            existingWorkItems.every(item => orderWorkItemIds.includes(item.id));
        
        let workItemsToUse;
        if (hasMatchingWorkItems) {
            // Behalte den vorherigen Stand, aber füge neue Items hinzu falls vorhanden
            const existingIds = existingWorkItems.map(item => item.id);
            const newItems = (order.work_items || [])
                .filter(item => !existingIds.includes(item.id))
                .map(item => ({
                    id: item.id,
                    description: item.description,
                    completed: false,
                    not_completed_reason: ''
                }));
            workItemsToUse = [...existingWorkItems, ...newItems];
        } else {
            // Keine passenden work_items vorhanden - initialisiere neu
            workItemsToUse = (order.work_items || []).map(item => ({
                id: item.id,
                description: item.description,
                completed: false,  // Muss vom Benutzer aktiv als erledigt markiert werden
                not_completed_reason: ''
            }));
        }
        
        setCompleteData(prev => ({
            ...prev,
            started_at: prev.started_at || '08:00',
            finished_at: timeStr,
            labor_hours: prev.labor_hours || '',
            travel_hours: prev.travel_hours || '0',
            notes: prev.notes || '',
            work_items: workItemsToUse,
            helper_user_ids: prev.helper_user_ids?.length > 0 ? prev.helper_user_ids : (order.helper_user_ids || [])
        }));
        setShowCompleteModal(true);
    };

    const calculateLaborHours = (start, end) => {
        if (!start || !end) return;
        const [startH, startM] = start.split(':').map(Number);
        const [endH, endM] = end.split(':').map(Number);
        if (isNaN(startH) || isNaN(endH)) return;
        
        let diff = (endH + endM/60) - (startH + startM/60);
        if (diff < 0) diff += 24;
        
        setCompleteData(prev => ({ ...prev, labor_hours: Math.round(diff * 10) / 10 }));
    };

    const handleCompleteOrder = async () => {
        // Validate work items
        const hasIncompleteWithoutReason = completeData.work_items.some(
            item => !item.completed && !item.not_completed_reason?.trim()
        );
        
        if (hasIncompleteWithoutReason) {
            toast.error('Bitte Grund für nicht erledigte Positionen angeben');
            return;
        }

        // Only validate labor hours for HOURLY billing type
        const isHourly = order.billing_type !== 'FIXED';
        if (isHourly && !completeData.labor_hours) {
            toast.error('Bitte Arbeitsstunden eingeben');
            return;
        }

        setCompleting(true);
        try {
            // First update helper_user_ids if changed
            if (JSON.stringify(completeData.helper_user_ids) !== JSON.stringify(order.helper_user_ids || [])) {
                await updateOrder(id, { helper_user_ids: completeData.helper_user_ids });
            }
            
            await completeOrder(id, {
                started_at: isHourly ? completeData.started_at : null,
                finished_at: isHourly ? completeData.finished_at : null,
                labor_hours: isHourly ? parseFloat(completeData.labor_hours) : 0,
                travel_hours: isHourly ? (parseFloat(completeData.travel_hours) || 0) : 0,
                notes: completeData.notes,
                work_item_completions: completeData.work_items.map(item => ({
                    id: item.id,
                    completed: item.completed,
                    not_completed_reason: item.completed ? null : item.not_completed_reason
                })),
                additional_work: additionalWork.filter(w => w.description.trim()),
                used_materials: usedMaterials.filter(m => m.name.trim()),
                images: []
            });
            
            toast.success('Auftrag abgeschlossen!');
            setShowCompleteModal(false);
            
            // Reset additional fields
            setAdditionalWork([]);
            setUsedMaterials([]);
            
            // Check if there are incomplete work items and ask about follow-up order
            const incomplete = completeData.work_items.filter(item => !item.completed);
            if (incomplete.length > 0) {
                setIncompleteWorkItems(incomplete);
                setShowFollowUpDialog(true);
            }
            
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Abschließen');
        } finally {
            setCompleting(false);
        }
    };
    
    const handleCreateFollowUpOrder = async () => {
        setCreatingFollowUp(true);
        try {
            const followUpData = {
                customer_id: order.customer_id,
                title: `Folgeauftrag: ${order.title}`,
                description: `Folgeauftrag von ${formatOrderNo(order.order_no)}\n\nNicht erledigte Positionen:\n${incompleteWorkItems.map(item => `- ${item.description}: ${item.not_completed_reason}`).join('\n')}`,
                service_address: order.service_address,
                work_items: incompleteWorkItems.map(item => ({
                    description: item.description
                })),
                billing_type: order.billing_type || 'EFFORT',
                priority: order.priority || 'MEDIUM'
            };
            
            const response = await createOrder(followUpData);
            toast.success('Folgeauftrag erstellt!');
            setShowFollowUpDialog(false);
            setIncompleteWorkItems([]);
            
            // Navigate to the new order
            navigate(`/orders/${response.data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Erstellen des Folgeauftrags');
        } finally {
            setCreatingFollowUp(false);
        }
    };
    
    // Additional work functions
    const addAdditionalWork = () => {
        setAdditionalWork([...additionalWork, { description: '', notes: '' }]);
    };
    
    const updateAdditionalWork = (index, field, value) => {
        const updated = [...additionalWork];
        updated[index][field] = value;
        setAdditionalWork(updated);
    };
    
    const removeAdditionalWork = (index) => {
        setAdditionalWork(additionalWork.filter((_, i) => i !== index));
    };
    
    // Used materials functions
    const addUsedMaterial = () => {
        setUsedMaterials([...usedMaterials, { name: '', quantity: '', notes: '' }]);
    };
    
    const updateUsedMaterial = (index, field, value) => {
        const updated = [...usedMaterials];
        updated[index][field] = value;
        setUsedMaterials(updated);
    };
    
    const removeUsedMaterial = (index) => {
        setUsedMaterials(usedMaterials.filter((_, i) => i !== index));
    };

    const openMap = () => {
        const address = order.service_address || order.customer?.billing_address;
        if (address) {
            window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
        }
    };

    const startEditing = () => {
        setEditData({
            title: order.title || '',
            description: order.description || '',
            service_address: order.service_address || '',
            planned_date: order.planned_date || '',
            deadline: order.deadline || '',
            external_order_no: order.external_order_no || '',
            work_items: order.work_items?.map(item => ({ description: item.description })) || [],
            helper_user_ids: order.helper_user_ids || [],
            materials: order.materials || []
        });
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditData({});
    };

    const addWorkItem = () => {
        setEditData({
            ...editData,
            work_items: [...editData.work_items, { description: '' }]
        });
    };

    const updateWorkItem = (index, description) => {
        const newItems = [...editData.work_items];
        newItems[index] = { description };
        setEditData({ ...editData, work_items: newItems });
    };

    const removeWorkItem = (index) => {
        const newItems = editData.work_items.filter((_, i) => i !== index);
        setEditData({ ...editData, work_items: newItems });
    };

    // Material functions
    const addMaterial = () => {
        setEditData({
            ...editData,
            materials: [...(editData.materials || []), { name: '', quantity: '', notes: '' }]
        });
    };

    const updateMaterial = (index, field, value) => {
        const newMaterials = [...(editData.materials || [])];
        newMaterials[index] = { ...newMaterials[index], [field]: value };
        setEditData({ ...editData, materials: newMaterials });
    };

    const removeMaterial = (index) => {
        const newMaterials = (editData.materials || []).filter((_, i) => i !== index);
        setEditData({ ...editData, materials: newMaterials });
    };

    const addHelper = (userId) => {
        if (!userId || editData.helper_user_ids?.includes(userId)) return;
        setEditData({
            ...editData,
            helper_user_ids: [...(editData.helper_user_ids || []), userId]
        });
    };

    const removeHelper = (userId) => {
        setEditData({
            ...editData,
            helper_user_ids: (editData.helper_user_ids || []).filter(id => id !== userId)
        });
    };

    const getHelperName = (userId) => {
        const user = allEmployees.find(e => e.id === userId) || employees.find(e => e.id === userId);
        return user?.name || 'Unbekannt';
    };

    // Use allEmployees for helper selection (includes those without smartphone)
    // Exclude the assigned employee from the helper list
    const availableHelpers = allEmployees.filter(e => 
        !(editData.helper_user_ids || []).includes(e.id) && 
        e.id !== order?.assigned_to_user_id
    );

    const handleSave = async () => {
        setSaving(true);
        try {
            // Filter out empty work items and materials
            const dataToSend = {
                ...editData,
                work_items: editData.work_items.filter(item => item.description.trim()),
                materials: (editData.materials || []).filter(m => m.name.trim())
            };
            await updateOrder(id, dataToSend);
            toast.success('Änderungen gespeichert');
            setIsEditing(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    if (!order) return null;

    return (
        <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="order-detail-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <span className="order-no text-primary text-lg">{formatOrderNo(order.order_no)}</span>
                            <StatusBadge status={order.status} />
                        </div>
                        <h1 className="text-2xl md:text-3xl font-bold">{order.title}</h1>
                        <p className="text-muted-foreground mt-1">{order.customer?.name}</p>
                        {order.external_order_no && (
                            <p className="text-sm text-muted-foreground">Ext. Nr.: {order.external_order_no}</p>
                        )}
                    </div>
                </div>

                {/* Actions */}
                {isAdmin() && (
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
                            <Download className="h-4 w-4 mr-2" />
                            PDF erstellen
                        </Button>
                        {!isEditing && (
                            <Button variant="outline" onClick={startEditing} data-testid="edit-btn">
                                <Edit2 className="h-4 w-4 mr-2" />
                                Bearbeiten
                            </Button>
                        )}
                        {order.status === 'ASSIGNED' && (
                            <Button 
                                onClick={async () => {
                                    try {
                                        await startOrder(id);
                                        toast.success('Auftrag gestartet');
                                        loadData();
                                    } catch (err) {
                                        toast.error('Fehler beim Starten');
                                    }
                                }}
                                data-testid="start-order-btn"
                            >
                                <Play className="h-4 w-4 mr-2" />
                                Auftrag starten
                            </Button>
                        )}
                        {(order.status === 'IN_PROGRESS' || order.status === 'ASSIGNED') && (
                            <Button 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={openCompleteModal}
                                data-testid="complete-order-btn"
                            >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Auftrag abschließen
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {isEditing ? (
                /* Edit Mode */
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Auftragsdetails bearbeiten</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="edit-title">Titel *</Label>
                                <Input
                                    id="edit-title"
                                    value={editData.title}
                                    onChange={(e) => setEditData({...editData, title: e.target.value})}
                                    data-testid="edit-title-input"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="edit-external-order-no">Externe Auftragsnummer</Label>
                                    <Input
                                        id="edit-external-order-no"
                                        value={editData.external_order_no}
                                        onChange={(e) => setEditData({...editData, external_order_no: e.target.value})}
                                        data-testid="edit-external-order-no-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-planned-date">Geplantes Datum</Label>
                                    <div className="relative">
                                        <Input
                                            id="edit-planned-date"
                                            type="date"
                                            value={editData.planned_date}
                                            onChange={(e) => setEditData({...editData, planned_date: e.target.value})}
                                            data-testid="edit-planned-date-input"
                                            className={editData.planned_date ? 'pr-10' : ''}
                                        />
                                        {editData.planned_date && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-destructive/10"
                                                onClick={() => setEditData({...editData, planned_date: ''})}
                                            >
                                                <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-deadline">Erledigen bis</Label>
                                    <div className="relative">
                                        <Input
                                            id="edit-deadline"
                                            type="date"
                                            value={editData.deadline || ''}
                                            onChange={(e) => setEditData({...editData, deadline: e.target.value})}
                                            data-testid="edit-deadline-input"
                                            className={editData.deadline ? 'pr-10' : ''}
                                        />
                                        {editData.deadline && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 hover:bg-destructive/10"
                                                onClick={() => setEditData({...editData, deadline: ''})}
                                            >
                                                <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-service-address">Leistungsadresse</Label>
                                <Textarea
                                    id="edit-service-address"
                                    value={editData.service_address}
                                    onChange={(e) => setEditData({...editData, service_address: e.target.value})}
                                    rows={2}
                                    data-testid="edit-service-address-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-description">Beschreibung</Label>
                                <Textarea
                                    id="edit-description"
                                    value={editData.description}
                                    onChange={(e) => setEditData({...editData, description: e.target.value})}
                                    rows={3}
                                    data-testid="edit-description-input"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-lg">Arbeitspositionen</CardTitle>
                            <Button type="button" variant="outline" size="sm" onClick={addWorkItem} data-testid="add-work-item-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Position hinzufügen
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {editData.work_items.length === 0 ? (
                                <p className="text-center py-8 text-muted-foreground">
                                    Keine Arbeitspositionen vorhanden.
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {editData.work_items.map((item, index) => (
                                        <div key={item.id || `work-item-${index}`} className="flex gap-2 items-start" data-testid={`edit-work-item-${index}`}>
                                            <span className="flex items-center justify-center w-8 h-10 text-sm font-medium text-muted-foreground">
                                                {index + 1}.
                                            </span>
                                            <Input
                                                value={item.description}
                                                onChange={(e) => updateWorkItem(index, e.target.value)}
                                                placeholder="z.B. Rasen mähen, Hecke schneiden..."
                                                className="flex-1"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeWorkItem(index)}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Helper Workers (Edit Mode) */}
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Users className="h-5 w-5" />
                                Mitwirkende Mitarbeiter
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Mitarbeiter hinzufügen</Label>
                                <Select onValueChange={addHelper} value="">
                                    <SelectTrigger data-testid="helper-select">
                                        <SelectValue placeholder="Mitarbeiter auswählen..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableHelpers.map(emp => (
                                            <SelectItem key={emp.id} value={emp.id}>
                                                {emp.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {(editData.helper_user_ids || []).length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {editData.helper_user_ids.map(userId => (
                                        <div 
                                            key={userId}
                                            className="flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                                        >
                                            <span>{getHelperName(userId)}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeHelper(userId)}
                                                className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-muted-foreground">
                                Mitarbeiter, die bei diesem Auftrag mitgewirkt haben.
                            </p>
                        </CardContent>
                    </Card>

                    {/* Materials (Edit Mode) */}
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Package className="h-5 w-5" />
                                Materialien
                            </CardTitle>
                            <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                onClick={addMaterial}
                                data-testid="add-material-btn"
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                Material hinzufügen
                            </Button>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {(editData.materials || []).length === 0 ? (
                                <p className="text-sm text-muted-foreground text-center py-4">
                                    Keine Materialien hinzugefügt
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {editData.materials.map((material, index) => (
                                        <div key={material.id || `material-${index}`} className="flex gap-2 items-start p-3 bg-muted/50 rounded-lg">
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                                                <Input
                                                    placeholder="Material Name *"
                                                    value={material.name}
                                                    onChange={(e) => updateMaterial(index, 'name', e.target.value)}
                                                    data-testid={`material-name-${index}`}
                                                />
                                                <Input
                                                    placeholder="Menge (z.B. 5 Stück)"
                                                    value={material.quantity || ''}
                                                    onChange={(e) => updateMaterial(index, 'quantity', e.target.value)}
                                                    data-testid={`material-quantity-${index}`}
                                                />
                                                <Input
                                                    placeholder="Bemerkung"
                                                    value={material.notes || ''}
                                                    onChange={(e) => updateMaterial(index, 'notes', e.target.value)}
                                                    data-testid={`material-notes-${index}`}
                                                />
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => removeMaterial(index)}
                                                className="text-destructive hover:text-destructive"
                                                data-testid={`remove-material-${index}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <p className="text-xs text-muted-foreground">
                                Fügen Sie verwendete Materialien hinzu (Name, Menge, Bemerkung).
                            </p>
                        </CardContent>
                    </Card>

                    <div className="flex gap-4 justify-end">
                        <Button variant="outline" onClick={cancelEditing} data-testid="cancel-edit-btn">
                            <X className="h-4 w-4 mr-2" />
                            Abbrechen
                        </Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-edit-btn">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Speichern
                        </Button>
                    </div>
                </div>
            ) : (
                /* View Mode */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Info Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Card className="card-hover cursor-pointer" onClick={openMap}>
                                <CardContent className="p-4 flex items-start gap-3">
                                    <MapPin className="h-5 w-5 text-primary mt-0.5" />
                                    <div>
                                        <p className="text-sm text-muted-foreground">Leistungsadresse</p>
                                        <p className="font-medium">{order.service_address || order.customer?.billing_address || '-'}</p>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-4 flex items-start gap-3">
                                    <Calendar className="h-5 w-5 text-primary mt-0.5" />
                                    <div>
                                        <p className="text-sm text-muted-foreground">Geplantes Datum</p>
                                        <p className="font-medium">{formatDate(order.planned_date) || 'Nicht festgelegt'}</p>
                                    </div>
                                </CardContent>
                            </Card>
                            {order.deadline && (
                                <Card>
                                    <CardContent className="p-4 flex items-start gap-3">
                                        <Clock className="h-5 w-5 text-orange-500 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Erledigen bis</p>
                                            <p className="font-medium text-orange-600">{formatDate(order.deadline)}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* Description */}
                        {order.description && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Beschreibung</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="whitespace-pre-wrap">{order.description}</p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Work Items */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <FileText className="h-5 w-5" />
                                    Arbeitspositionen ({order.work_items?.length || 0})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {!order.work_items || order.work_items.length === 0 ? (
                                    <p className="text-center py-6 text-muted-foreground">Keine Arbeitspositionen definiert</p>
                                ) : (
                                    <div className="space-y-3">
                                        {order.work_items.map((item, i) => (
                                            <div 
                                                key={item.id || i} 
                                                className={`flex items-start gap-3 p-3 rounded-sm border ${
                                                    item.completed ? 'bg-green-50 border-green-200' : 
                                                    item.completed === false && item.not_completed_reason ? 'bg-red-50 border-red-200' : 
                                                    'bg-muted/50'
                                                }`}
                                                data-testid={`work-item-display-${i}`}
                                            >
                                                <div className="flex-shrink-0 mt-0.5">
                                                    {item.completed ? (
                                                        <Check className="h-5 w-5 text-green-600" />
                                                    ) : item.not_completed_reason ? (
                                                        <XCircle className="h-5 w-5 text-red-600" />
                                                    ) : (
                                                        <span className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-muted-foreground/50 text-xs text-muted-foreground">
                                                            {i + 1}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <p className={`font-medium ${item.completed ? 'text-green-700' : item.not_completed_reason ? 'text-red-700' : ''}`}>
                                                        {item.description}
                                                    </p>
                                                    {item.not_completed_reason && (
                                                        <p className="text-sm text-red-600 mt-1">
                                                            Grund: {item.not_completed_reason}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Helper Workers (View Mode) */}
                        {order.helper_users && order.helper_users.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Users className="h-5 w-5" />
                                        Mitwirkende Mitarbeiter ({order.helper_users.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex flex-wrap gap-2">
                                        {order.helper_users.map(helper => (
                                            <div 
                                                key={helper.id}
                                                className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm"
                                            >
                                                <User className="h-4 w-4" />
                                                <span>{helper.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Materials (View Mode) */}
                        {order.materials && order.materials.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Package className="h-5 w-5" />
                                        Materialien ({order.materials.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {order.materials.map((material, index) => (
                                            <div 
                                                key={material.id || `material-view-${index}`}
                                                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Package className="h-4 w-4 text-primary" />
                                                    <div>
                                                        <p className="font-medium">{material.name}</p>
                                                        {(material.quantity || material.notes) && (
                                                            <p className="text-sm text-muted-foreground">
                                                                {material.quantity && <span>{material.quantity}</span>}
                                                                {material.quantity && material.notes && <span> • </span>}
                                                                {material.notes && <span>{material.notes}</span>}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Work Report */}
                        {order.work_report && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Arbeitsbericht</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Beginn</p>
                                            <p className="font-medium">{order.work_report.started_at}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Ende</p>
                                            <p className="font-medium">{order.work_report.finished_at}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Arbeitsstunden</p>
                                            <p className="font-medium">{order.work_report.labor_hours} h</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-muted-foreground">Fahrtzeit</p>
                                            <p className="font-medium">{order.work_report.travel_hours} h</p>
                                        </div>
                                    </div>
                                    {order.work_report.notes && (
                                        <div>
                                            <p className="text-sm text-muted-foreground mb-1">Zusätzliche Arbeiten / Bemerkungen</p>
                                            <p className="whitespace-pre-wrap bg-muted/50 p-3 rounded-sm">{order.work_report.notes}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Project Assignment - For completed orders */}
                        {order.status === 'DONE' && isAdmin() && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <FolderKanban className="h-5 w-5" />
                                        Projekt
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {order.project_id ? (
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{order.project_name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Diesem Projekt zugeordnet
                                                </p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => navigate(`/projects/${order.project_id}`)}
                                                >
                                                    Öffnen
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedProjectId('');
                                                        setShowProjectDialog(true);
                                                    }}
                                                >
                                                    Ändern
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between">
                                            <p className="text-muted-foreground">
                                                Keinem Projekt zugeordnet
                                            </p>
                                            <Button
                                                variant="outline"
                                                onClick={() => setShowProjectDialog(true)}
                                                data-testid="assign-project-btn"
                                            >
                                                <FolderKanban className="h-4 w-4 mr-2" />
                                                Projekt zuordnen
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Images */}
                        {order.images && order.images.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Bilder ({order.images.length})</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {order.images.map((img, i) => (
                                            <div 
                                                key={img.id || i} 
                                                className="relative aspect-square rounded-lg overflow-hidden border bg-muted cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                                                onClick={() => { setSelectedImage(img); setImageIndex(i); }}
                                                data-testid={`order-image-${i}`}
                                            >
                                                <img
                                                    src={img.data}
                                                    alt={img.filename}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1">
                                                    <p className="truncate">{img.filename}</p>
                                                    <p className="text-white/70">{img.upload_type === 'CREATION' ? 'Erstellung' : 'Abschluss'}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Image Viewer Modal */}
                        {selectedImage && (
                            <div 
                                className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
                                onClick={() => setSelectedImage(null)}
                            >
                                <button 
                                    className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                                    onClick={() => setSelectedImage(null)}
                                >
                                    <X className="h-8 w-8" />
                                </button>
                                
                                {order.images.length > 1 && (
                                    <>
                                        <button 
                                            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newIndex = imageIndex > 0 ? imageIndex - 1 : order.images.length - 1;
                                                setImageIndex(newIndex);
                                                setSelectedImage(order.images[newIndex]);
                                            }}
                                        >
                                            <ChevronLeft className="h-8 w-8" />
                                        </button>
                                        <button 
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newIndex = imageIndex < order.images.length - 1 ? imageIndex + 1 : 0;
                                                setImageIndex(newIndex);
                                                setSelectedImage(order.images[newIndex]);
                                            }}
                                        >
                                            <ChevronRight className="h-8 w-8" />
                                        </button>
                                    </>
                                )}
                                
                                <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                                    <img
                                        src={selectedImage.data}
                                        alt={selectedImage.filename}
                                        className="max-w-full max-h-[85vh] object-contain"
                                    />
                                    <div className="text-center text-white mt-2">
                                        <p>{selectedImage.filename}</p>
                                        <p className="text-sm text-white/70">
                                            {imageIndex + 1} / {order.images.length} • {selectedImage.upload_type === 'CREATION' ? 'Bei Erstellung' : 'Bei Abschluss'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Assignment */}
                        {isAdmin() && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Zuweisung</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <User className="h-5 w-5 text-muted-foreground" />
                                        <div>
                                            <p className="text-sm text-muted-foreground">Zugewiesen an</p>
                                            <p className="font-medium">{order.assigned_user?.name || 'Nicht zugewiesen'}</p>
                                        </div>
                                    </div>
                                    {/* Always allow reassignment for admins */}
                                    <div className="space-y-2">
                                        <Label className="text-sm text-muted-foreground">
                                            {order.assigned_to_user_id ? 'Neu zuweisen' : 'Zuweisen'}
                                        </Label>
                                        <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                                            <SelectTrigger data-testid="assign-employee-select">
                                                <SelectValue placeholder="Mitarbeiter wählen..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="unassign">— Niemand (Zuweisung entfernen) —</SelectItem>
                                                {employees.map(emp => (
                                                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Button 
                                            className="w-full" 
                                            onClick={handleAssign} 
                                            disabled={assigning || !selectedEmployee}
                                            data-testid="assign-btn"
                                        >
                                            {assigning && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                            {selectedEmployee === 'unassign' ? 'Zuweisung entfernen' : 'Zuweisen'}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* History */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Historie</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {order.history?.map((entry, i) => (
                                        <div key={entry.id || i} className="flex gap-3">
                                            <div className="flex flex-col items-center">
                                                <div className="h-2 w-2 rounded-full bg-primary" />
                                                {i < order.history.length - 1 && <div className="flex-1 w-px bg-border mt-1" />}
                                            </div>
                                            <div className="pb-4">
                                                <p className="text-sm font-medium">{entry.action}</p>
                                                <p className="text-xs text-muted-foreground">{entry.user_name} · {formatDateTime(entry.timestamp)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {/* Complete Order Modal */}
            <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Auftrag abschließen</DialogTitle>
                        <DialogDescription>
                            Geben Sie die Arbeitszeiten ein und markieren Sie die erledigten Positionen.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        {/* Work Items */}
                        {completeData.work_items.length > 0 && (
                            <div className="space-y-3">
                                <Label className="text-base font-semibold">Arbeitspositionen</Label>
                                {completeData.work_items.map((item, index) => (
                                    <div 
                                        key={item.id || index}
                                        className={`p-3 border rounded-lg ${item.completed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <Switch
                                                checked={item.completed}
                                                onCheckedChange={(checked) => {
                                                    const newItems = [...completeData.work_items];
                                                    newItems[index].completed = checked;
                                                    if (checked) newItems[index].not_completed_reason = '';
                                                    setCompleteData({ ...completeData, work_items: newItems });
                                                }}
                                            />
                                            <div className="flex-1">
                                                <p className="font-medium">{item.description}</p>
                                                {!item.completed && (
                                                    <Input
                                                        className="mt-2"
                                                        placeholder="Grund warum nicht erledigt *"
                                                        value={item.not_completed_reason}
                                                        onChange={(e) => {
                                                            const newItems = [...completeData.work_items];
                                                            newItems[index].not_completed_reason = e.target.value;
                                                            setCompleteData({ ...completeData, work_items: newItems });
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Times - Only for HOURLY billing type */}
                        {order.billing_type !== 'FIXED' && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Beginn</Label>
                                        <Input
                                            type="time"
                                            value={completeData.started_at}
                                            onChange={(e) => {
                                                setCompleteData({ ...completeData, started_at: e.target.value });
                                                calculateLaborHours(e.target.value, completeData.finished_at);
                                            }}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Ende</Label>
                                        <Input
                                            type="time"
                                            value={completeData.finished_at}
                                            onChange={(e) => {
                                                setCompleteData({ ...completeData, finished_at: e.target.value });
                                                calculateLaborHours(completeData.started_at, e.target.value);
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Arbeitsstunden *</Label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            placeholder="Auto-berechnet"
                                            value={completeData.labor_hours}
                                            onChange={(e) => setCompleteData({ ...completeData, labor_hours: e.target.value })}
                                            className="bg-blue-50 border-blue-200"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Fahrtzeit (h)</Label>
                                        <Input
                                            type="number"
                                            step="0.5"
                                            value={completeData.travel_hours}
                                            onChange={(e) => setCompleteData({ ...completeData, travel_hours: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Info for FIXED billing type */}
                        {order.billing_type === 'FIXED' && (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    <strong>Pauschalauftrag</strong> - Keine Zeiterfassung erforderlich
                                </p>
                            </div>
                        )}

                        {/* Helper Workers in Complete Modal */}
                        <div className="space-y-3 border-t pt-4">
                            <Label className="flex items-center gap-2">
                                <Users className="h-4 w-4" />
                                Mitwirkende Mitarbeiter
                            </Label>
                            <Select 
                                onValueChange={(v) => {
                                    if (v && !completeData.helper_user_ids.includes(v)) {
                                        setCompleteData({
                                            ...completeData,
                                            helper_user_ids: [...completeData.helper_user_ids, v]
                                        });
                                    }
                                }} 
                                value=""
                            >
                                <SelectTrigger data-testid="complete-helper-select">
                                    <SelectValue placeholder="Mitarbeiter hinzufügen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {allEmployees.filter(e => 
                                        !completeData.helper_user_ids.includes(e.id) && 
                                        e.id !== order.assigned_to_user_id
                                    ).map(emp => (
                                        <SelectItem key={emp.id} value={emp.id}>
                                            {emp.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {completeData.helper_user_ids.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {completeData.helper_user_ids.map(userId => {
                                        const emp = allEmployees.find(e => e.id === userId) || employees.find(e => e.id === userId);
                                        return (
                                            <div 
                                                key={userId}
                                                className="flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                                            >
                                                <span>{emp?.name || 'Unbekannt'}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setCompleteData({
                                                        ...completeData,
                                                        helper_user_ids: completeData.helper_user_ids.filter(id => id !== userId)
                                                    })}
                                                    className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
                                                >
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label>Notizen (optional)</Label>
                            <Textarea
                                placeholder="Zusätzliche Bemerkungen..."
                                value={completeData.notes}
                                onChange={(e) => setCompleteData({ ...completeData, notes: e.target.value })}
                            />
                        </div>
                        
                        {/* Additional Work Section */}
                        <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-base font-medium">Zusätzliche Arbeiten</Label>
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    onClick={addAdditionalWork}
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Hinzufügen
                                </Button>
                            </div>
                            {additionalWork.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Keine zusätzlichen Arbeiten erfasst</p>
                            ) : (
                                <div className="space-y-3">
                                    {additionalWork.map((work, index) => (
                                        <div key={`add-work-${index}`} className="flex gap-2 items-start p-3 bg-muted/50 rounded-lg">
                                            <div className="flex-1 space-y-2">
                                                <Input
                                                    placeholder="Beschreibung der Arbeit *"
                                                    value={work.description}
                                                    onChange={(e) => updateAdditionalWork(index, 'description', e.target.value)}
                                                />
                                                <Input
                                                    placeholder="Anmerkung (optional)"
                                                    value={work.notes || ''}
                                                    onChange={(e) => updateAdditionalWork(index, 'notes', e.target.value)}
                                                />
                                            </div>
                                            <Button 
                                                type="button"
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => removeAdditionalWork(index)}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* Used Materials Section */}
                        <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-base font-medium">Verwendete Materialien</Label>
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    onClick={addUsedMaterial}
                                >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Hinzufügen
                                </Button>
                            </div>
                            {usedMaterials.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Keine Materialien erfasst</p>
                            ) : (
                                <div className="space-y-3">
                                    {usedMaterials.map((material, index) => (
                                        <div key={`material-${index}`} className="flex gap-2 items-start p-3 bg-muted/50 rounded-lg">
                                            <div className="flex-1 space-y-2">
                                                <div className="flex gap-2">
                                                    <Input
                                                        placeholder="Material/Produkt *"
                                                        value={material.name}
                                                        onChange={(e) => updateUsedMaterial(index, 'name', e.target.value)}
                                                        className="flex-1"
                                                    />
                                                    <Input
                                                        placeholder="Menge"
                                                        value={material.quantity || ''}
                                                        onChange={(e) => updateUsedMaterial(index, 'quantity', e.target.value)}
                                                        className="w-28"
                                                    />
                                                </div>
                                            </div>
                                            <Button 
                                                type="button"
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => removeUsedMaterial(index)}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCompleteModal(false)}>
                            Abbrechen
                        </Button>
                        <Button 
                            onClick={handleCompleteOrder}
                            disabled={completing}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {completing ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Wird abgeschlossen...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Auftrag abschließen
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Project Assignment Dialog */}
            <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Projekt zuordnen</DialogTitle>
                        <DialogDescription>
                            Ordnen Sie diesen Auftrag einem Projekt zu, um Zeiten und Arbeiten zusammenzufassen.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label>Projekt auswählen</Label>
                        <Select
                            value={selectedProjectId}
                            onValueChange={setSelectedProjectId}
                        >
                            <SelectTrigger className="mt-2" data-testid="project-select">
                                <SelectValue placeholder="Projekt auswählen..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Kein Projekt</SelectItem>
                                {projects.map(project => (
                                    <SelectItem key={project.id} value={project.id}>
                                        {project.name} ({project.customer_name})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {projects.length === 0 && (
                            <p className="text-sm text-muted-foreground mt-2">
                                Keine offenen Projekte vorhanden. 
                                <Button
                                    variant="link"
                                    className="p-0 h-auto ml-1"
                                    onClick={() => navigate('/projects')}
                                >
                                    Projekt erstellen
                                </Button>
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowProjectDialog(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleAssignProject} disabled={assigningProject}>
                            {assigningProject && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            {order?.project_id && selectedProjectId === 'none' ? 'Zuordnung entfernen' : 'Zuordnen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Follow-up Order Dialog */}
            <Dialog open={showFollowUpDialog} onOpenChange={setShowFollowUpDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Copy className="h-5 w-5 text-orange-500" />
                            Folgeauftrag erstellen?
                        </DialogTitle>
                        <DialogDescription>
                            Es gibt nicht erledigte Arbeitspositionen. Möchten Sie einen Folgeauftrag erstellen?
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">Nicht erledigte Positionen:</Label>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {incompleteWorkItems.map((item, index) => (
                                <div key={item.id || `incomplete-${index}`} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="font-medium text-sm">{item.description}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Grund: {item.not_completed_reason}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <DialogFooter className="flex gap-2 sm:gap-0">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setShowFollowUpDialog(false);
                                setIncompleteWorkItems([]);
                            }}
                        >
                            Nein, danke
                        </Button>
                        <Button 
                            onClick={handleCreateFollowUpOrder} 
                            disabled={creatingFollowUp}
                            className="bg-orange-500 hover:bg-orange-600"
                        >
                            {creatingFollowUp && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                            <Copy className="h-4 w-4 mr-2" />
                            Folgeauftrag erstellen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default OrderDetailPage;
